import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button, Container, Stack, Tabs, Text, Title } from '@mantine/core';

import { BrowseTab, DashboardTab, DismissDialog, QueueTab, SnoozeDialog } from '@/components/Inbox';
import { useAuth } from '@/contexts/AuthContext';
import {
  BROWSE_PAGE_SIZE,
  useBrowseEntities,
  useCaseDashboard,
  useInbox,
  useInboxGrouped,
  useInboxKinds,
  useInboxMutations,
} from '@/hooks';
import { useClaimCase } from '@/hooks/useClaimCase';
import { vnextApiClient } from '@/services/vnext-api';
import { DashboardView } from '@/types/caseDashboard';
import { InboxGroupedParams, InboxItem, InboxPriority, InboxState } from '@/types/inbox';

type Tab = 'queue' | 'browse' | 'dashboard';

const TAB_VALUES: Tab[] = ['queue', 'browse', 'dashboard'];

/** Codename groups per Queue-tab page (backend default matches). */
export const GROUPS_PER_PAGE = 20;

function isTab(value: string | null): value is Tab {
  return value !== null && (TAB_VALUES as string[]).includes(value);
}

interface InboxPageProps {
  /** Default tab when the URL doesn't carry one. Used by the
   * `/inbox/browse` route shortcut (and the legacy `/inbox/upcoming`
   * redirect, which lands on the queue — snoozed lives there now). */
  initialTab?: Tab;
}

/**
 * The Inbox — the analyst's work queue (2026-07 redesign).
 *
 * Three tabs, URL-persisted (`?tab=queue|browse|dashboard`):
 * - **Queue** — split-pane triage: Needs you / Waiting / Snoozed
 *   sections on the left, the selected item's preview pane on the
 *   right. Selection persists via `?item=`.
 * - **Browse** — the case/entity lookup table.
 * - **Dashboard** — the ops view of every active case (the GTI-Hub
 *   parallel of the Backoffice "Project Ops Dashboard" sheet), with
 *   `?view=completed` and `?analyst=` slices.
 *
 * The page owns the list hooks, dialog state, the case-claim mutation,
 * and seen-state: explicitly selecting a row marks it seen server-side
 * (activating the previously dormant `POST /inbox/seen`), clearing its
 * new-dot for the whole team.
 */
export function InboxPage({ initialTab }: Readonly<InboxPageProps>) {
  const auth = useAuth();
  const isAdmin = !!auth?.user?.isAdministrator;
  const currentUserId = auth?.user?.id != null ? String(auth.user.id) : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // ---------------------------------------------------------------
  // URL-backed state
  // ---------------------------------------------------------------
  const tabParam = searchParams.get('tab');
  const tab: Tab = isTab(tabParam) ? tabParam : (initialTab ?? 'queue');
  const selectedId = searchParams.get('item');

  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);
  const filters: InboxGroupedParams = useMemo(
    () => ({
      kind: searchParams.get('kind') ?? undefined,
      priority: (searchParams.get('priority') as InboxPriority | null) ?? undefined,
      search: searchParams.get('q') ?? undefined,
      page,
      pageSize: GROUPS_PER_PAGE,
    }),
    [searchParams, page]
  );
  const browseSearch = searchParams.get('q') ?? '';
  const browseMode = searchParams.get('mode') === 'all' ? 'all' : 'cases';
  const dashboardView: DashboardView =
    searchParams.get('view') === 'completed' ? 'completed' : 'active';
  const dashboardAnalyst = searchParams.get('analyst');
  const dashboardClient = searchParams.get('client');
  const dashboardQa = searchParams.get('qa');

  const updateParams = useCallback(
    (mut: (next: URLSearchParams) => void, opts?: { replace?: boolean }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mut(next);
          return next;
        },
        opts?.replace ? { replace: true } : undefined
      );
    },
    [setSearchParams]
  );

  const handleTabChange = (next: string | null) => {
    if (!next || !isTab(next)) {
      return;
    }
    updateParams((p) => p.set('tab', next));
  };

  // Filter / search commits REPLACE the history entry: they are
  // ephemeral view state, and (for search especially) stepping the
  // Back button through every settled value is noise. Tab switches,
  // pagination and row selection still push.
  const handleFiltersChange = (next: InboxGroupedParams) => {
    updateParams(
      (p) => {
        if (next.kind) {
          p.set('kind', next.kind);
        } else {
          p.delete('kind');
        }
        if (next.priority) {
          p.set('priority', next.priority);
        } else {
          p.delete('priority');
        }
        if (next.search) {
          p.set('q', next.search);
        } else {
          p.delete('q');
        }
        // A filter change re-scopes the group list — restart at page 1.
        p.delete('page');
      },
      { replace: true }
    );
  };

  const handlePageChange = (next: number) => {
    updateParams((p) => {
      if (next > 1) {
        p.set('page', String(next));
      } else {
        p.delete('page');
      }
    });
  };

  const handleSearchChange = (next: string) => {
    updateParams(
      (p) => {
        if (next) {
          p.set('q', next);
        } else {
          p.delete('q');
        }
      },
      { replace: true }
    );
  };

  // ---------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------
  // Every list hook is gated on its own tab: `enabled: false` means no
  // fetch at all, so one tab's param churn (the shared `?q=`) can never
  // fan out to another tab's endpoints.
  const onQueue = tab === 'queue';
  const grouped = useInboxGrouped(filters, { poll: onQueue, enabled: onQueue });
  const snoozed = useInbox({ state: 'snoozed' as InboxState }, { poll: onQueue, enabled: onQueue });
  // Browse is server-paginated; the page resets whenever the search or
  // mode changes (both URL-owned) so a filter never lands on a stale page.
  const [browsePage, setBrowsePage] = useState(1);
  useEffect(() => {
    setBrowsePage(1);
  }, [browseSearch, browseMode]);
  const browse = useBrowseEntities({
    enabled: tab === 'browse',
    q: browseSearch,
    mode: browseMode,
    page: browsePage,
    pageSize: BROWSE_PAGE_SIZE,
  });
  const dashboard = useCaseDashboard({ view: dashboardView, enabled: tab === 'dashboard' });
  const kinds = useInboxKinds();
  const mutations = useInboxMutations();

  const refreshAll = useCallback(async () => {
    await Promise.all([grouped.refetch(), snoozed.refetch()]);
  }, [grouped, snoozed]);

  // ---------------------------------------------------------------
  // Selection + seen-state
  // ---------------------------------------------------------------
  // Optimistic seen-ids so the new-dot clears immediately (the server
  // truth arrives with the next poll).
  const [locallySeen, setLocallySeen] = useState<ReadonlySet<string>>(new Set());
  const markingRef = useRef<Set<string>>(new Set());

  const handleSelectItem = useCallback(
    (item: InboxItem, opts: { explicit: boolean }) => {
      updateParams((p) => p.set('item', item.id));
      // Only an explicit click marks seen — the default-selection
      // effect must not silently clear the team's new-dots.
      if (opts.explicit && item.lastSeenAt === null && !markingRef.current.has(item.id)) {
        markingRef.current.add(item.id);
        setLocallySeen((prev) => new Set(prev).add(item.id));
        void vnextApiClient.markInboxSeen([item.id]).catch(() => {
          markingRef.current.delete(item.id);
        });
      }
    },
    [updateParams]
  );

  const { claim } = useClaimCase();
  const handleClaim = useCallback(
    async (familyKey: string, userId: string | null) => {
      await claim(familyKey, userId);
      await refreshAll();
    },
    [claim, refreshAll]
  );

  // Dashboard-tab claims refresh the dashboard read, not the (disabled)
  // queue hooks — refetch() bypasses the enabled gate by design.
  const dashboardRefetch = dashboard.refetch;
  const handleDashboardClaim = useCallback(
    async (familyKey: string, userId: string | null) => {
      await claim(familyKey, userId);
      await dashboardRefetch();
    },
    [claim, dashboardRefetch]
  );

  // QA slots live on the REPORT (not the family) — one write per slot.
  const handleAssignQa = useCallback(
    async (reportId: string, slot: 'first' | 'second', userId: string | null) => {
      await vnextApiClient.setReportQaReviewers(
        reportId,
        slot === 'first' ? { firstQaUserId: userId } : { secondQaUserId: userId }
      );
      await dashboardRefetch();
    },
    [dashboardRefetch]
  );

  // ---------------------------------------------------------------
  // Dialog state
  // ---------------------------------------------------------------
  const [snoozeTarget, setSnoozeTarget] = useState<InboxItem | null>(null);
  const [dismissTarget, setDismissTarget] = useState<InboxItem | null>(null);

  const handleSnoozeConfirm = async (wakeAt: Date, reason: string | null) => {
    if (!snoozeTarget) {
      return;
    }
    await mutations.snooze(snoozeTarget.id, {
      wakeAt: wakeAt.toISOString(),
      reason,
    });
    await refreshAll();
  };

  const handleDismissConfirm = async (reason: string) => {
    if (!dismissTarget) {
      return;
    }
    await mutations.dismiss(dismissTarget.id, { reason });
    await refreshAll();
  };

  const handleResolve = async (item: InboxItem) => {
    await mutations.resolve(item.id, {});
    await refreshAll();
  };

  // Per-item assignee override — hands ONE item to someone without
  // moving case ownership.
  const handleItemAssign = useCallback(
    async (item: InboxItem, userId: string | null) => {
      await mutations.assign(item.id, { userId });
      await refreshAll();
    },
    [mutations, refreshAll]
  );

  if (!isAdmin) {
    // Belt-and-braces — the router's <AdminRoute> already gates this
    // page. Keeping the check here protects against future direct
    // mounts.
    return (
      <Container py="lg">
        <Text>Admin access required.</Text>
        <Button onClick={() => navigate('/')}>Home</Button>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Stack gap={0}>
          <Title order={2}>Inbox</Title>
          <Text c="dimmed" size="sm">
            Actionable items across every enquiry, report, and conversation you own.
          </Text>
        </Stack>

        <Tabs value={tab} onChange={handleTabChange} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="queue">Queue</Tabs.Tab>
            <Tabs.Tab value="browse">Browse</Tabs.Tab>
            <Tabs.Tab value="dashboard">Dashboard</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="queue" pt="md">
            <QueueTab
              groups={grouped.groups}
              snoozedItems={snoozed.items}
              groupTotal={grouped.groupTotal}
              page={page}
              onPageChange={handlePageChange}
              isLoading={grouped.isLoading}
              error={grouped.error}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              availableKinds={kinds.kinds}
              currentUserId={currentUserId}
              selectedId={selectedId}
              onSelectItem={handleSelectItem}
              locallySeen={locallySeen}
              onClaim={handleClaim}
              onItemAssign={handleItemAssign}
              onCodenameRenamed={refreshAll}
              onRequestSnooze={setSnoozeTarget}
              onRequestDismiss={setDismissTarget}
              onRequestResolve={handleResolve}
            />
          </Tabs.Panel>

          <Tabs.Panel value="browse" pt="md">
            <BrowseTab
              rows={browse.rows}
              total={browse.total}
              page={browsePage}
              onPageChange={setBrowsePage}
              isLoading={browse.isLoading}
              error={browse.error}
              search={browseSearch}
              onSearchChange={handleSearchChange}
              mode={browseMode}
              onModeChange={(next) =>
                updateParams((p) => {
                  if (next === 'all') {
                    p.set('mode', 'all');
                  } else {
                    p.delete('mode');
                  }
                })
              }
              onCodenameRenamed={browse.refetch}
            />
          </Tabs.Panel>

          <Tabs.Panel value="dashboard" pt="md">
            <DashboardTab
              data={dashboard.data}
              isLoading={dashboard.isLoading}
              error={dashboard.error}
              view={dashboardView}
              onViewChange={(next) =>
                updateParams((p) => {
                  if (next === 'completed') {
                    p.set('view', 'completed');
                  } else {
                    p.delete('view');
                  }
                })
              }
              analystFilter={dashboardAnalyst}
              onAnalystFilterChange={(next) =>
                updateParams((p) => {
                  if (next) {
                    p.set('analyst', next);
                  } else {
                    p.delete('analyst');
                  }
                })
              }
              clientFilter={dashboardClient}
              onClientFilterChange={(next) =>
                updateParams((p) => {
                  if (next) {
                    p.set('client', next);
                  } else {
                    p.delete('client');
                  }
                })
              }
              qaFilter={dashboardQa}
              onQaFilterChange={(next) =>
                updateParams((p) => {
                  if (next) {
                    p.set('qa', next);
                  } else {
                    p.delete('qa');
                  }
                })
              }
              search={browseSearch}
              onSearchChange={handleSearchChange}
              onSaveNotes={dashboard.saveNotes}
              onRefresh={dashboard.refetch}
              currentUserId={currentUserId}
              onClaim={handleDashboardClaim}
              onAssignQa={handleAssignQa}
            />
          </Tabs.Panel>
        </Tabs>
      </Stack>

      <SnoozeDialog
        opened={snoozeTarget !== null}
        onClose={() => setSnoozeTarget(null)}
        onConfirm={handleSnoozeConfirm}
        itemTitle={snoozeTarget?.title}
      />
      <DismissDialog
        opened={dismissTarget !== null}
        onClose={() => setDismissTarget(null)}
        onConfirm={handleDismissConfirm}
        itemTitle={dismissTarget?.title}
      />
    </Container>
  );
}
